import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "utils";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Grid,
	TextField,
	InputAdornment,
	IconButton,
	Tooltip
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { ADMIN_HOME_ROUTE } from "features/home/home-routes";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PageHeaderAddButton,
	PageHeaderListActions,
	PaginationHeader,
	PopupSnackbar
} from "frame/components";
import EquipmentListing from "./equipment-listing";
import EquipmentModal from "./equipment-modal";
import { EQUIPMENT_ROUTE, EQUIPMENT_USAGE_ADD_ROUTE } from "../actions/types";

const useStyles = makeStyles((theme) => ({
	root: {},
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
	search: {
		display: "flex"
	},
}));

const Equipment = ({
	equipment,
	isLoadingList,
	onList,
	onAdd,
	onSave,
	onRemove
}) => {
	const classes = useStyles();

	const { history } = useRouter();
	const equipmentUsageRecordingEnabled = useSelector(
		({ featureFlags }) =>
			featureFlags?.equipment === true &&
			featureFlags?.equipmentUsageRecording === true
	);
	const [selectedEquipment, setSelectedEquipment] = useState();
	const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [shouldRefresh, setShouldRefresh] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [searchFilter, setSearchFilter] = useState("");

	const filteredEquipment = useMemo(() => {
		const sortActiveFirst = (list) =>
			[...list].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0));

		if (!searchFilter) {
			return sortActiveFirst(equipment);
		}

		const term = searchFilter.toString().toLowerCase().trim();
		if (!term) {
			return sortActiveFirst(equipment);
		}

		const matches = (value) => {
			if (value === null || value === undefined) {
				return false;
			}

			// Dates & numbers -> string
			return value
				.toString()
				.toLowerCase()
				.indexOf(term) !== -1;
		};

		const filtered = equipment.filter((item) => {
			// Search across all known fields for OrganisationEquipmentDTO
			return (
				matches(item.equipment) ||
				matches(item.type) ||
				matches(item.make) ||
				matches(item.model) ||
				matches(item.serialRego) ||
				matches(item.vin) ||
				matches(item.assetNumber) ||
				matches(item.fuelType) ||
				matches(item.maintenanceType) ||
				matches(item.warrantyPeriod) ||
				matches(item.usualLocationSiteName) || // joined field from API
				matches(item.currentHoursKms) ||
				matches(item.currentHoursKmsDate) ||
				matches(item.nextWof) ||
				matches(item.nextReg) ||
				matches(item.nextRUCs) ||
				matches(item.nextService) ||
				matches(item.hourlyRate) ||
				// Fallback: stringified object
				matches(JSON.stringify(item))
			);
		});
		return sortActiveFirst(filtered);
	}, [equipment, searchFilter]);

	const onEquipmentEdit = (equipmentId) => {
		setSelectedEquipment({
			...equipment.find(e => e.id === equipmentId)
		});
		setShowAddEquipmentModal(true);
	};

	const onEquipmentDelete = (equipmentId) => {
		onRemove(equipmentId);
	};

	const onModalSave = (equipmentItem) => {
		if (equipmentItem.id) {
			onSave(equipmentItem);
		}
		else {
			onAdd(equipmentItem);
		}
		setShowAddEquipmentModal(false);
		setSuccessMessage("Equipment saved successfully.");
		// Trigger refresh after a short delay to ensure API call completes
		setShouldRefresh(true);
	};

	useEffect(() => {
		onList();
	}, [onList]);

	// Refresh list after successful save
	useEffect(() => {
		if (shouldRefresh) {
			const timer = setTimeout(() => {
				onList();
				setShouldRefresh(false);
			}, 500); // Wait 500ms for API call to complete
			return () => clearTimeout(timer);
		}
	}, [shouldRefresh, onList]);

	return (
		<Page className={classes.root} title="Equipment">
			<ScrollToTopOnMount />
							<PageHeaderAddButton
					title="Equipment"
					addTabTitle="New"
					onAdd={() => {
						setSelectedEquipment(undefined);
						setShowAddEquipmentModal(true);
					}}
					secondaryTabTitle={
						equipmentUsageRecordingEnabled ? "Record Usage" : undefined
					}
					onSecondaryAction={
						equipmentUsageRecordingEnabled
							? () =>
									history.push(EQUIPMENT_USAGE_ADD_ROUTE, {
										from: EQUIPMENT_ROUTE,
									})
							: undefined
					}
					staffUserHide={true}
					backActionPath={ADMIN_HOME_ROUTE}
					titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
							searchShowLabel="Show search"
							searchHideLabel="Hide search"
						/>
					}
				>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="equipment-search"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={() => setSearchFilter("")}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(event) => setSearchFilter(event.target.value)}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={"Search by any equipment field"}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderAddButton>

			<PageContent pad>
				<div className={classes.body}>
					<PaginationHeader
						hidePagination
						pagination={{
							totalNumberOfRecords: filteredEquipment ? filteredEquipment.length : 0
						}}
					/>
					<EquipmentListing
						equipment={filteredEquipment}
						isLoading={isLoadingList}
						onEdit={onEquipmentEdit}
						onDelete={onEquipmentDelete}
					/>
				</div>
			</PageContent>
			<EquipmentModal
				equipment={selectedEquipment}
				isOpen={showAddEquipmentModal}
				onSubmit={onModalSave}
				onClose={() => setShowAddEquipmentModal(false)}
			/>
			<PopupSnackbar
				message={successMessage}
				coloured
				success
				onExited={() => setSuccessMessage("")}
			/>
		</Page>
	);
};

Equipment.propTypes = {
	equipment: PropTypes.array.isRequired,
	isLoadingList: PropTypes.bool.isRequired,
	onList: PropTypes.func.isRequired,
	onAdd: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired
};

export default Equipment;

